/* eslint-disable sonarjs/no-duplicate-string */
import {
  EventSummary,
  EventWithCustomField,
  ExhibitorProfilesPackNumber,
  MinimalEventForParticipation,
} from "@ac-expo/api-types";
import { EventScheduleType } from "@ac-expo/utils";
import { BagIcon, BalloonIcon, FileTextIcon, Icon, MapPinIcon } from "@phosphor-icons/react";
import { FileType } from "@prisma/client";
import { useEffect, useState } from "react";
import { cn } from "../../../utils";
import ProgramPanel from "../event/Panel/ProgramPanel";
import { PlanningEvent } from "../planning";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../Tabs";
import DocumentsPanel from "./panels/DocumentsPanel";
import ParticipationPanel from "./panels/ParticipationsPanel";
import ProductPanel from "./panels/ProductsPanel";
import { CondensedStructureType, StructurePanel } from "./StructureDetails";

const StructureTabTrigger = ({
  value,
  icon: Icon,
  label,
  isActive,
  hidden,
}: {
  value: StructurePanel;
  icon: Icon;
  label: string;
  isActive: boolean;
  hidden?: boolean;
}) => {
  if (hidden) return null;

  return (
    <TabsTrigger
      value={value}
      className={cn("grow flex-col border-b-2 border-transparent max-[450px]:px-0", {
        "text-blue-500": isActive,
        "text-gray-400": !isActive,
      })}
    >
      <Icon size={24} weight={isActive ? "fill" : "regular"} aria-hidden="true" />
      <p className="text-center text-wrap">{label}</p>
    </TabsTrigger>
  );
};

export const StructureDetailsSection = ({
  structure,
  participations,
  activePanel,
  onPanelChange,
  getFileUrl,
  onOpenActivityList,
  onEventCardClick,
  eventSchedules,
  exhibitorPackNumber,
}: {
  structure: CondensedStructureType;
  participations: EventWithCustomField<EventSummary>[] | MinimalEventForParticipation[];
  activePanel?: StructurePanel;
  onPanelChange?: (to: StructurePanel) => void;
  getFileUrl: (key?: string, type?: FileType) => string;
  onOpenActivityList: (planningEvent: PlanningEvent[]) => void;
  onEventCardClick: (eventId: string) => void;
  eventSchedules?: EventScheduleType[];
  exhibitorPackNumber?: ExhibitorProfilesPackNumber | null;
}) => {
  const { products, activities } = structure;
  const canShowDocuments =
    exhibitorPackNumber === undefined ||
    (exhibitorPackNumber !== null && exhibitorPackNumber >= 2);
  const documents = canShowDocuments ? structure.documents : [];

  const [currentPanel, setCurrentPanel] = useState<StructurePanel>("participation");

  useEffect(() => {
    if (activePanel !== undefined && activePanel !== currentPanel) {
      setCurrentPanel(activePanel);
    }
  }, [activePanel, currentPanel]);

  const handleValueChange = (value: string) => {
    const newPanel = value as StructurePanel;
    setCurrentPanel(newPanel);
    if (onPanelChange) {
      onPanelChange(newPanel);
    }
  };

  return (
    <Tabs value={currentPanel} onValueChange={handleValueChange} variant="default">
      <TabsList className="w-full h-fit py-0 px-0 min-[450px]:px-4 overflow-y-auto items-end">
        <StructureTabTrigger
          value="participation"
          icon={MapPinIcon}
          label="Participations"
          isActive={currentPanel === "participation"}
        />
        <StructureTabTrigger
          value="product"
          icon={BagIcon}
          label="Produits présentés"
          isActive={currentPanel === "product"}
          hidden={products.length === 0}
        />
        <StructureTabTrigger
          value="animation"
          icon={BalloonIcon}
          label="Animations Stand"
          isActive={currentPanel === "animation"}
          hidden={activities.length === 0}
        />
        <StructureTabTrigger
          value="document"
          icon={FileTextIcon}
          label="Documents"
          isActive={currentPanel === "document"}
          hidden={documents.length === 0}
        />
      </TabsList>

      <div className="border border-gray-200 bg-gray-50 rounded-2xl">
        <TabsContent value="participation" className="size-full gap-6 overflow-y-auto p-6">
          <ParticipationPanel
            getFileUrl={getFileUrl}
            participations={participations}
            onEventCardClick={onEventCardClick}
          />
        </TabsContent>

        <TabsContent value="product" className="size-full gap-6 overflow-y-auto p-6 mt-0">
          <ProductPanel
            getFileUrl={getFileUrl}
            products={structure.products ?? []}
            exhibitorPackNumber={exhibitorPackNumber}
          />
        </TabsContent>

        <TabsContent value="animation" className="size-full gap-6 overflow-y-auto p-6">
          <ProgramPanel
            activities={structure.activities ?? []}
            openActivityList={onOpenActivityList}
            eventSchedules={eventSchedules}
          />
        </TabsContent>

        <TabsContent value="document" className="size-full gap-6 overflow-y-auto p-6 mt-0">
          <DocumentsPanel getFileUrl={getFileUrl} documents={structure.documents ?? []} />
        </TabsContent>
      </div>
    </Tabs>
  );
};
